import type { AppData, CardState, DeckId, ReviewHistoryEntry } from '../../types';
import { createDefaultCardState } from '../../domain/srs/srs';

const KEY_BASE = 'klausurphase.v1';
let activeUserId: string | null = null;
let cache: AppData | null = null;
let silent = false;

// Daten werden pro User unter einem eigenen Key gespeichert, damit auf einem
// geteilten Browser nie Daten eines anderen Nutzers geladen (oder beim Seeden
// eines frischen Accounts hochgeladen) werden.
function storageKey(): string | null {
  return activeUserId ? `${KEY_BASE}.u.${activeUserId}` : null;
}

// Muss nach Login/Registrierung (mit User-ID) bzw. Logout (mit null)
// aufgerufen werden, bevor load()/save() benutzt werden.
export function setActiveUser(userId: string | null) {
  if (userId === activeUserId) return;
  activeUserId = userId;
  cache = null;
  try {
    // Alten, nicht user-gebundenen Key nie wieder einlesen (er kann von einem
    // anderen Nutzer stammen). Als Backup umbenennen statt löschen.
    const legacy = localStorage.getItem(KEY_BASE);
    if (legacy !== null) {
      localStorage.setItem(`${KEY_BASE}.backup`, legacy);
      localStorage.removeItem(KEY_BASE);
    }
  } catch { /* ignore */ }
  window.dispatchEvent(new Event('storage:user-changed'));
}

export function defaults(): AppData {
  return {
    decks: {},
    history: [],
    settings: {
      theme: 'light',
      maxNewPerDay: 0,
      targetRetention: 0.9,
      boostWeak: true,
      examMode: true,
    },
    lastSession: null,
    streak: { count: 0, lastDay: null },
  };
}

export function load(): AppData {
  if (cache) return cache;
  const key = storageKey();
  if (!key) {
    cache = defaults();
    return cache;
  }
  try {
    const raw = localStorage.getItem(key);
    cache = raw ? mergeDefaults(JSON.parse(raw)) : defaults();
  } catch {
    cache = defaults();
  }
  return cache;
}

export function replaceCache(next: Partial<AppData>) {
  silent = true;
  try {
    cache = mergeDefaults(next);
    save();
  } finally {
    silent = false;
  }
  window.dispatchEvent(new Event('storage:replaced'));
}

export function save() {
  if (!cache) return;
  const key = storageKey();
  if (key) {
    try {
      localStorage.setItem(key, JSON.stringify(cache));
    } catch (error) {
      console.warn('Speichern fehlgeschlagen', error);
    }
  }
  if (!silent) window.dispatchEvent(new Event('storage:saved'));
}

export function getDeckState(deckId: DeckId) {
  const data = load();
  data.decks[deckId] ??= { cards: {}, examDate: null };
  return data.decks[deckId]!;
}

export function getCardState(deckId: DeckId, cardId: string): CardState {
  const deck = getDeckState(deckId);
  deck.cards[cardId] ??= createDefaultCardState();
  return deck.cards[cardId];
}

export function setCardState(deckId: DeckId, cardId: string, state: CardState) {
  getDeckState(deckId).cards[cardId] = state;
  save();
}

export function setCardSuspended(deckId: DeckId, cardId: string, suspended: boolean) {
  const state = getCardState(deckId, cardId);
  state.suspended = suspended;
  save();
}

export function logReview(entry: ReviewHistoryEntry) {
  const data = load();
  data.history.push(entry);
  if (data.history.length > 5000) data.history = data.history.slice(-5000);
  save();
}

export function bumpStreak() {
  const data = load();
  const today = todayIso();
  const last = data.streak.lastDay;
  if (last === today) return;
  if (last === yesterdayIso()) data.streak.count += 1;
  else data.streak.count = 1;
  data.streak.lastDay = today;
  save();
}

export function exportAll() {
  return JSON.stringify(load(), null, 2);
}

export function importAll(json: string) {
  cache = mergeDefaults(JSON.parse(json));
  save();
}

export function reset() {
  cache = defaults();
  save();
}

export function todayIso() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}

export function yesterdayIso() {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
}

function mergeDefaults(input: unknown): AppData {
  const base = defaults();
  if (!input || typeof input !== 'object') return base;
  const obj = input as Partial<AppData>;
  return {
    ...base,
    ...obj,
    decks: obj.decks ?? base.decks,
    history: Array.isArray(obj.history) ? obj.history : base.history,
    settings: { ...base.settings, ...(obj.settings ?? {}) },
    streak: { ...base.streak, ...(obj.streak ?? {}) },
  };
}
